import { GENAI_API_BASE_URL } from '../config';

// Types for API service
export interface ApiRequestConfig {
  endpoint: string;
  headers?: Record<string, string>;
  params?: Record<string, any>;
  body?: any;
}

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
}

export interface ApiError {
  message: string;
  status?: number;
  statusText?: string;
  data?: any;
}

// Default headers
const defaultHeaders = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
};

/**
 * Build URL with query parameters
 */
const buildURL = (endpoint: string, params?: Record<string, any>): string => {
  const baseUrl = endpoint.startsWith('http') ? endpoint : `${GENAI_API_BASE_URL}${endpoint}`;
  const url = new URL(baseUrl);
  
  if (params) {
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, String(params[key]));
      }
    });
  }
  
  return url.toString();
};

/**
 * Merge headers with defaults
 */
const mergeHeaders = (customHeaders?: Record<string, string>): Record<string, string> => {
  return { ...defaultHeaders, ...customHeaders };
};

/**
 * Handle API response
 */
const handleResponse = async <T>(response: Response): Promise<ApiResponse<T>> => {
  let data: T;
  
  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text() as unknown as T;
    }
  } catch (error) {
    data = null as unknown as T;
  }

  if (!response.ok) {
    const apiError: ApiError = {
      message: response.statusText || 'Request failed',
      status: response.status,
      statusText: response.statusText,
      data
    };
    throw apiError;
  }

  return {
    data,
    status: response.status,
    statusText: response.statusText
  };
};

/**
 * GET request
 */
export const get = async <T = any>(config: ApiRequestConfig): Promise<ApiResponse<T>> => {
  try {
    const url = buildURL(config.endpoint, config.params);
    const headers = mergeHeaders(config.headers);

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    return await handleResponse<T>(response);
  } catch (error) {
    throw error;
  }
};

/**
 * POST request
 */
export const post = async <T = any>(config: ApiRequestConfig): Promise<ApiResponse<T>> => {
  try {
    const url = buildURL(config.endpoint, config.params);
    const headers = mergeHeaders(config.headers);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: config.body ? JSON.stringify(config.body) : undefined,
    });

    return await handleResponse<T>(response);
  } catch (error) {
    throw error;
  }
};

/**
 * PUT request
 */
export const put = async <T = any>(config: ApiRequestConfig): Promise<ApiResponse<T>> => {
  try {
    const url = buildURL(config.endpoint, config.params);
    
    // Check if body is FormData - if so, don't stringify and let browser set Content-Type
    const isFormData = config.body instanceof FormData;
    const headers = isFormData 
      ? { ...config.headers } // Don't merge default headers for FormData (browser sets Content-Type with boundary)
      : mergeHeaders(config.headers);
    
    // Remove Content-Type if it's FormData so browser can set it with boundary
    if (isFormData && headers['Content-Type']) {
      delete headers['Content-Type'];
    }

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: config.body ? (isFormData ? config.body : JSON.stringify(config.body)) : undefined,
    });

    return await handleResponse<T>(response);
  } catch (error) {
    throw error;
  }
};

/**
 * PATCH request
 */
export const patch = async <T = any>(config: ApiRequestConfig): Promise<ApiResponse<T>> => {
  try {
    const url = buildURL(config.endpoint, config.params);
    const headers = mergeHeaders(config.headers);

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: config.body ? JSON.stringify(config.body) : undefined,
    });

    return await handleResponse<T>(response);
  } catch (error) {
    throw error;
  }
};

/**
 * DELETE request
 */
export const del = async <T = any>(config: ApiRequestConfig): Promise<ApiResponse<T>> => {
  try {
    const url = buildURL(config.endpoint, config.params);
    const headers = mergeHeaders(config.headers);

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
      body: config.body ? JSON.stringify(config.body) : undefined,
    });

    return await handleResponse<T>(response);
  } catch (error) {
    throw error;
  }
};

// Export as default object
const apiService = {
  get,
  post,
  put,
  patch,
  delete: del,
};

export default apiService;
